import { lzQueryDecode, queryEncode, safeQueryDecode } from '@/utils/condition-query-encoding';
import { isEmptyString } from '@/utils/string';
import { hasUnconvertedLegacyRows } from './normalize-legacy-filter-wire';
import {
  isFiltersEnvelope,
  normalizePeopleFilterWire,
  repairCampaignWildcardBitmapNames,
} from './people-filter-format';
import { asJsonObject, type JsonObject, type JsonValue } from './wire-types';

// The parse boundary between the encoded URL param and the wire layer: the
// param decodes to arbitrary JSON, and everything past this point traverses
// it as JsonValue.
function toJsonValue(value: unknown): JsonValue {
  if (value == null || typeof value === 'string' || typeof value === 'number' || typeof value === 'boolean') {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(toJsonValue);
  }
  if (typeof value === 'object') {
    const out: JsonObject = {};
    for (const [key, entry] of Object.entries(value)) {
      out[key] = toJsonValue(entry);
    }
    return out;
  }
  return null;
}

/**
 * Decodes the `filters` URL param for the wire layer. An absent or
 * undecodable param yields the empty tree — the builder's blank slate — and
 * anything decodable comes back as plain JSON for the normalizers to walk.
 */
export function decodePeopleFiltersParam(encoded: string | null): JsonValue {
  if (encoded == null || isEmptyString(encoded)) {
    return { and: [] };
  }
  return toJsonValue(safeQueryDecode(encoded));
}

// True for the bare legacy-array wire: every element a `type`-tagged record.
// The check exists because the tolerant decoder also returns `[]` for an
// *undecodable* param — normalizing that to `{ and: [] }` would turn garbage
// into a match-everyone query.
function isLegacyArrayWire(decoded: JsonValue[]): boolean {
  return decoded.every((row) => {
    const record = asJsonObject(row);
    return record != null && typeof record.type === 'string';
  });
}

// `safeQueryDecode`'s failure value ([]) is indistinguishable from a genuinely
// encoded empty array, and the read boundary treats the two differently: a
// decodable lz- wire re-encodes to Base64 while garbage keeps its bytes. Keep
// the failure visible as null. (JSON `null` also lands here; it carries no
// wire either boundary recognizes, so collapsing it into "undecodable" keeps
// its raw-passthrough behavior.)
function decodedFiltersParam(filters: string): JsonValue | null {
  try {
    return toJsonValue(lzQueryDecode(filters));
  } catch {
    return null;
  }
}

// The API boundaries (the search query, the export payload) read only the
// Base64 encoding, so a decodable lz- param re-encodes even when its wire is
// unrecognized — passing the lz- bytes through would hand the endpoint a
// prefix it can't read. A wire with no Base64 spelling here (a primitive, or
// an array holding non-records) keeps its bytes.
function base64Reencoded(filters: string, decoded: JsonValue): string {
  if (!filters.startsWith('lz-')) {
    return filters;
  }
  if (Array.isArray(decoded)) {
    const records = decoded.flatMap((row) => {
      const record = asJsonObject(row);
      return record == null ? [] : [record];
    });
    return records.length === decoded.length ? queryEncode(records) : filters;
  }
  const record = asJsonObject(decoded);
  return record == null ? filters : queryEncode(record);
}

/**
 * The `filters` param as the search API should receive it. The read
 * boundaries (the list query directly, the export payload via
 * {@link exportablePeopleFilters}, the bulk-delete payload via
 * {@link normalizedPeopleFilterEnvelope}) all route through the same
 * normalization so they see the same audience:
 *
 * - The bare legacy-array deep-link wire converts to the proto tree.
 * - Campaign-wildcard bitmap names broken during the AUD-4375 window are
 *   repaired, so an affected deep link queries correctly on first load
 *   instead of matching nobody until its first edit.
 *
 * An untouched wire keeps its **original bytes** — anything unrecognized
 * (garbage, foreign shapes) passes through raw, and the URL itself is never
 * rewritten; only the queried copy is normalized. The one exception is an
 * lz-compressed param: the endpoints read only the Base64 form, so a
 * decodable lz- wire re-encodes even when unrecognized.
 */
// fallow-ignore-next-line complexity -- the read boundary's wire dispatch; one guard per dialect
export function queryablePeopleFilters(filters: string | null): string | null {
  if (filters == null || isEmptyString(filters)) {
    return filters;
  }
  const decoded = decodedFiltersParam(filters);
  if (decoded == null) {
    return filters;
  }

  if (Array.isArray(decoded)) {
    if (decoded.length === 0 || !isLegacyArrayWire(decoded)) {
      return base64Reencoded(filters, decoded);
    }
    return encodeEnvelope(normalizePeopleFilterWire(decoded)) ?? filters;
  }

  const record = asJsonObject(decoded);
  if (record == null || !isFiltersEnvelope(record)) {
    return base64Reencoded(filters, decoded);
  }
  const repaired = repairCampaignWildcardBitmapNames(record);
  // Reference identity is the "untouched" signal: keep the original bytes.
  // The one exception is an lz-compressed param — the API and the Ember app
  // read only the Base64 form, so it re-encodes even when untouched.
  if (repaired === record && !filters.startsWith('lz-')) {
    return filters;
  }
  return encodeEnvelope(repaired) ?? filters;
}

/**
 * The `filters` param as the export payload should send it: the same
 * queryable bytes as {@link queryablePeopleFilters}, except that a wire whose
 * queryable form carries a null row ({@link hasUnconvertedLegacyRows} — a
 * legacy row the conversion could not model, or a hand-authored null) refuses
 * with null. A null row constrains nobody, so forwarding it could export far
 * more profiles than the audience the wire named — the same refusal contract
 * the bulk-delete boundary applies, because an export is a data-exposure
 * mutation of its own (a CSV of whichever audience the bytes end up naming).
 *
 * Callers must distinguish this refusal from "no search active": for a
 * non-empty param, null means the export must not run — not "send no filter".
 */
export function exportablePeopleFilters(filters: string | null): string | null {
  const queryable = queryablePeopleFilters(filters);
  if (queryable == null || isEmptyString(queryable)) {
    return queryable;
  }
  const decoded = decodedFiltersParam(queryable);
  if (decoded != null && hasUnconvertedLegacyRows(decoded)) {
    return null;
  }
  return queryable;
}

// The leaf keys this surface models (wire-types.ts `Filter`). A hand-built
// URL can carry a single bare leaf instead of a group envelope (every builder
// emits a root group), and the endpoints take it: the delete body's `filters`
// is one proto Filter message, and the list query forwards the same bare
// bytes. Refusing a leaf here while the list shows its audience would strand
// the Delete action at confirm time.
const FILTER_LEAF_KEYS = [
  'attribute',
  'device_attribute',
  'segment',
  'seg_attr',
  'newsletter_recipients',
  'named_bitmap',
] as const;

function isRecognizedFilterLeaf(record: JsonObject): boolean {
  return FILTER_LEAF_KEYS.some((key) => key in record);
}

/**
 * The decoded, normalized proto filter, for payloads that send the tree as
 * JSON (bulk delete): a group envelope, or a single recognized leaf — both
 * shapes the delete endpoint's proto Filter message takes, and both wires the
 * list query forwards, so the mutation targets the audience the list showed.
 *
 * Null when no same-audience tree exists — callers treat that as "no valid
 * filter" rather than sending bytes with a different meaning:
 *
 * - the param doesn't decode to a wire the normalizer recognizes;
 * - the legacy conversion left a null row ({@link hasUnconvertedLegacyRows}):
 *   a null leaf constrains nobody, so forwarding it to an irreversible
 *   mutation could target far more than the audience the wire named.
 */
export function normalizedPeopleFilterEnvelope(filters: string | null): JsonObject | null {
  if (filters == null || isEmptyString(filters)) {
    return null;
  }
  const decoded = toJsonValue(safeQueryDecode(filters));
  if (Array.isArray(decoded) && (decoded.length === 0 || !isLegacyArrayWire(decoded))) {
    return null;
  }
  const normalized = asJsonObject(normalizePeopleFilterWire(decoded));
  if (normalized == null || hasUnconvertedLegacyRows(normalized)) {
    return null;
  }
  if (!isFiltersEnvelope(normalized) && !isRecognizedFilterLeaf(normalized)) {
    return null;
  }
  return normalized;
}

function encodeEnvelope(wire: JsonValue): string | null {
  const envelope = asJsonObject(wire);
  return envelope == null ? null : queryEncode(envelope);
}
